import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import {
  CurrentEnvironment,
  EventLoggerInstanceName,
  TEST_ENV_QUERY_PARAM,
} from '~/shared/constants/commonConstants'
import { getCurrentEnv } from '~/shared/helpers/getCurrentEnv'
import { getEnvironment } from '~/shared/helpers/getEnvironment'
import { eventLogger } from '~/shared/services/eventLogger'
import {
  selectCohortID,
  selectGiaApiKeys,
  selectLanguage,
} from '~/shared/store/common/selects'
import {
  selectStripeAccountId,
  selectStripeAccountName,
} from '~/shared/store/payment/selects'
import { selectUserUUID, selectUserUtmTags } from '~/shared/store/user/selects'

import { Amplitude } from '../lib/amplitude'
import { UserFlowTracker } from '../lib/userFlowTracker'

function getGIAAmplitudeOptions() {
  const btoaAuthCredentials = 'cHJveHktdXNlcjozQyk9Q3YncTd5L0A4Pjcp'

  return {
    apiEndpoint:
      getCurrentEnv() === CurrentEnvironment.PROD
        ? 'analytics.gismart.xyz/events'
        : 'analytics.gidev.xyz/events',
    headers: {
      Authorization: `Bearer ${btoaAuthCredentials}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    forceHttps: true,
  }
}

export const useGiaFlowTrackerInitialization = (): void => {
  const { search } = useLocation()
  const uuid = useSelector(selectUserUUID)
  const cohort = useSelector(selectCohortID)
  const giaApiKeys = useSelector(selectGiaApiKeys)
  const stripeAccountId = useSelector(selectStripeAccountId)
  const stripeAccountName = useSelector(selectStripeAccountName)
  const lng = useSelector(selectLanguage)
  const utmTags = useSelector(selectUserUtmTags)

  const [giaInstance, setGiaInstance] = useState<Amplitude | null>(null)
  const { isProdEnvironment } = getEnvironment()

  const URLParams = new URLSearchParams(search)
  const isTestEnvironment = URLParams.has(TEST_ENV_QUERY_PARAM)
  const AD_ATTRIBUTION_PARAMS = new Set([
    'campaign_id',
    'campaign_name',
    'adset_id',
    'adset_name',
    'ad_id',
    'ad_name',
    'publisher_site_id',
  ])
  const utmTagsFromUrl = Object.fromEntries(
    [...URLParams.entries()].filter(
      ([key]) => key.startsWith('utm_') || AD_ATTRIBUTION_PARAMS.has(key),
    ),
  )

  useEffect(() => {
    if (uuid && giaApiKeys) {
      const giaService = new Amplitude({
        apiKey: giaApiKeys[lng],
        instanceName: EventLoggerInstanceName.GIA,
        options: getGIAAmplitudeOptions(),
        userId: uuid,
        utmTags: utmTagsFromUrl.utm_source ? utmTagsFromUrl : utmTags,
        isTestEnvironment,
        isDevEnvironment: !isProdEnvironment,
      })

      const userFlowTracker = new UserFlowTracker({
        instanceName: EventLoggerInstanceName.USER_FLOW_TRACKER,
        userId: uuid,
      })

      giaService.configure({ cohort, language: lng })
      userFlowTracker.configure({ cohort })

      eventLogger.init(giaService, userFlowTracker)
      setGiaInstance(giaService)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giaApiKeys, uuid, lng])

  useEffect(() => {
    if (!stripeAccountId || !stripeAccountName) return

    giaInstance?.updateUserProperties(stripeAccountId, stripeAccountName)
  }, [giaInstance, stripeAccountId, stripeAccountName])
}
